import React, { useEffect, useState } from 'react';
import { useStyletron } from 'baseui';
import { KIND } from 'baseui/button';
import { ChevronDown } from 'baseui/icon';
import { toaster } from 'baseui/toast';
import { Overflow } from 'baseui-new/icon';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import BouncingEffect from 'components/BouncingEffect/BouncingEffect';
import { LayoutContent, LayoutFooter } from 'components/Common/Layout';
import ENGTButton from 'components/UI/ENGTButton/ENGTButton';
import Checkbox from 'components/UI/ENGTCheckBox/ENGTCheckBox';
import usePaginationHook from 'components/UI/ENGTPagination/PaginationHook';
import ENGTTable from 'components/UI/ENGTTable/ENGTTable';
import { ITableRowAction } from 'components/UI/ENGTTable/ENGTTableRowActions';
import ENGTToasterContainer from 'components/UI/ENGTToaster/ENGTToasterContainer';
import ENGTTooltip from 'components/UI/ENGTTooltip/ENGTTooltip';
import FormLabel from 'components/UI/Form/FormLabel/FormLabel';
import Loader from 'components/UI/Loader/Loader';
import ActionModal from 'components/UI/Modal/ActionModal/ActionModal';
import NoDataTemplate from 'components/UI/NoDataTemplate/NoDataTemplate';

import { AD_DETAILS_CONNECT, AD_DETAILS_LOGOUT, AD_DETAILS_NEXT, AD_DETAILS_PREVIOUS } from 'shared/consts/QAConsts';
import { IObjProps } from 'shared/consts/types';
import { BROADCAST_TYPE, CHANNELS, messageSortAllScreen } from 'shared/enum';
import { findEnv, hexToRgb, isValidResponseObject } from 'shared/helpers';
import NoDataEmoji from 'shared/icons/NoDataEmoji.svg';
import InfoIcon from 'shared/icons/PathBuilder/InfoIcon';

import { API as FB_API } from 'store/App/Configure/API/Deployment/Facebook/api';
import { appIdSelector, botKeyData, botRefSelector, uuidSelector } from 'store/Bot/selectors';
import { API } from 'store/Broadcast/api';
import { RootState } from 'store/rootReducer';
import { customerIdSelector } from 'store/Users/selectors';

import { ThreeColumnRouteLinks } from 'router/links/ThreeColumnRouteLinks';
import { TwoColumnRouteLinks } from 'router/links/TwoColumnRouteLinks';

import LoginComponent from '../../../Configure/Deployment/helpers/helpers';

import AdsSorting, { SORTING_OPTIONS } from './AdsSorting';
import ClickToWARetargetModal from './ClickToWARetargetModal';
import RetargetPlatformSelectionModal from './RetargetPlatformSelectionModal';

enum AD_STATUS {
	ACTIVE = 'ACTIVE',
	PAUSED = 'PAUSED',
	ARCHIVED = 'ARCHIVED',
	DELETED = 'DELETED',
}

const AD_DETAILS_LABELS = {
	CONNECTED_ADS: 'pages:broadcast.clickToWhatsappAds.adDetailsTab.connectedAds',
	CONNECTED_ADS_SELECTED: 'pages:broadcast.clickToWhatsappAds.adDetailsTab.connectedAdsSelected',
	NO_CONNECTED_ADS: 'pages:broadcast.clickToWhatsappAds.adDetailsTab.noConnectedAds',
	RECONFIGURE: 'pages:broadcast.clickToWhatsappAds.adDetailsTab.reconfigure',
	VIEW_MESSAGES: 'pages:broadcast.clickToWhatsappAds.adDetailsTab.viewMessages',
	LOGOUT_MODAL_HEADING: 'pages:broadcast.clickToWhatsappAds.adDetailsTab.logoutModalHeading',
	LOGOUT_MODAL_DESCRIPTION: 'pages:broadcast.clickToWhatsappAds.adDetailsTab.logoutModalDescription',
	AD_TYPE: 'pages:broadcast.clickToWhatsappAds.adDetailsTab.adType',
	AD_ACCOUNT_ID: 'pages:broadcast.clickToWhatsappAds.adDetailsTab.adAccountId',
	COST_PER_CLICK: 'pages:broadcast.clickToWhatsappAds.adDetailsTab.costPerClick',
	COST_PER_CLICK_TOOLTIP: 'pages:broadcast.clickToWhatsappAds.adDetailsTab.costPerClickTooltip',
	TOTAL_AMOUNT_SPENT: 'pages:broadcast.clickToWhatsappAds.adDetailsTab.totalAmountSpent',
	TOTAL_AMOUNT_SPENT_TOOLTIP: 'pages:broadcast.clickToWhatsappAds.adDetailsTab.totalAmountSpentTooltip',
	RETARGET: 'pages:broadcast.clickToWhatsappAds.retarget',
	VIEW_USERS: 'pages:broadcast.clickToWhatsappAds.viewUsers',
	EDIT: 'pages:broadcast.clickToWhatsappAds.edit',
	PAUSE: 'pages:broadcast.clickToWhatsappAds.pause',
	ARCHIVE: 'pages:broadcast.clickToWhatsappAds.archieve',
	DELETE: 'pages:broadcast.clickToWhatsappAds.delete',
	ACTIVATE: 'pages:broadcast.clickToWhatsappAds.activate',
};

const AD_TYPE = {
	AD: 'AD',
	POST: 'POST',
};

const ACCOUNT_CURRENCY_SYMBOLS: Record<string, string> = {
	AED: 'AED',
	AUD: 'A$',
	BRL: 'R$',
	CAD: 'C$',
	CHF: 'CHF',
	CNY: 'CN¥',
	EUR: '€',
	GBP: '£',
	HKD: 'HK$',
	IDR: 'Rp',
	INR: '₹',
	JPY: '¥',
	KRW: '₩',
	MXN: 'MX$',
	MYR: 'RM',
	NZD: 'NZ$',
	PHP: '₱',
	PLN: 'zł',
	SAR: '﷼',
	SEK: 'kr',
	SGD: 'S$',
	THB: '฿',
	TRY: '₺',
	TWD: 'NT$',
	USD: '$',
	VND: '₫',
	ZAR: 'R',
};

const AdDetailsTab = () => {
	const { t } = useTranslation(['common', 'pages']);
	const [css, theme]: [any, any] = useStyletron();
	const navigate = useNavigate();

	const [isLoading, setLoading] = useState<boolean>(true);
	const [isPlatformSelectionModalOpen, setPlatformSelectionModalOpen] = useState<boolean>(false);
	const [isRetargetModalOpen, setRetargetModalOpen] = useState<boolean>(false);
	const [isLogoutModalOpen, setLogoutModalOpen] = useState<boolean>(false);
	const [adDetails, setAdDetails] = useState<Array<IObjProps>>([]);
	const [segmentData, setSegmentData] = useState<IObjProps>();
	const [selectedAdId, setSelectedAdId] = useState<string>('');
	const [isAdAuthTokenPresent, setIsAdAuthTokenPresent] = useState<boolean>(false);
	const [fbmSelection, setFbmSelection] = useState<boolean>(false);
	const [fbmAccounts, setFbmAccounts] = useState<Array<IObjProps>>([]);
	const [selectedBusinessIds, setSelectedBusinessIds] = useState<Array<string>>([]);
	const [adsSelection, setAdsSelection] = useState<boolean>(false);
	const [adAccountsByBusiness, setAdAccountsByBusiness] = useState<Record<string, Array<IObjProps>>>({});
	const [selectedAdAccountIds, setSelectedAdAccountIds] = useState<Array<string>>([]);
	const [visibleAdAccountCountByBusiness, setVisibleAdAccountCountByBusiness] = useState<Record<string, number>>({});
	const [configuredAdAccounts, setConfiguredAdAccounts] = useState<Array<IObjProps>>([]);
	const [lastUpdatedOn, setLastUpdatedOn] = useState<string>('');
	const [adsAnalytics, displayAdsAnalytics] = useState<boolean>(false);
	const [syncing, setSyncing] = useState<boolean>(false);
	const [sort, setSorting] = useState<string>(SORTING_OPTIONS.AD_NAME);

	const { usersListLink } = TwoColumnRouteLinks;
	const ADS_MANAGER_URL = 'https://adsmanager.facebook.com/adsmanager/manage/ads/edit/standalone?selected_ad_ids=';
	const pageDefaultSize = 10;
	const DEFAULT_VISIBLE_AD_ACCOUNT_COUNT = 10;
	const MAX_PAGE_SIZE = 50;
	const AD_PAGE_SIZE_OPTIONS = [10, 25, 50];

	const tableOverrides = {
		wrapper: {
			marginLeft: 0,
			marginRight: 0,
			marginTop: '1rem',
			marginBottom: '3rem',
			overflowX: 'auto',
		},
		table: {
			overflow: 'auto',
			minWidth: 'max-content',
		},
	};

	const botKey = useSelector(botKeyData);
	const appId = useSelector(appIdSelector);
	const botRef = useSelector(botRefSelector);
	const customerId = useSelector(customerIdSelector);
	const uid = useSelector(uuidSelector);
	const isSimplifiedOneViewEnabled = useSelector(
		(state: RootState) => state.User.botAdmin.data.is_simplified_one_view_enabled
	);

	const sortOptions = [
		{
			name: t('pages:broadcast.clickToWhatsappAds.adDetailsTab.sorting.engagedUsersDesc'),
			value: SORTING_OPTIONS.LEADS_CAPTURED_WA_DESC,
		},
		{
			name: t('pages:broadcast.clickToWhatsappAds.adDetailsTab.sorting.engagedUsersAsc'),
			value: SORTING_OPTIONS.LEADS_CAPTURED_WA_ASC,
		},
		{
			name: t('pages:broadcast.clickToWhatsappAds.adDetailsTab.sorting.adName'),
			value: SORTING_OPTIONS.AD_NAME,
		},
		{
			name: t('pages:broadcast.clickToWhatsappAds.adDetailsTab.sorting.engagedInstaUserAsc'),
			value: SORTING_OPTIONS.LEADS_CAPTURED_INSTA_ASC,
		},
		{
			name: t('pages:broadcast.clickToWhatsappAds.adDetailsTab.sorting.engagedInstaUserDesc'),
			value: SORTING_OPTIONS.LEADS_CAPTURED_INSTA_DESC,
		},
	];

	const getStatusColorCode = (status: string) => {
		const statusColorCodeMap: IObjProps = {
			[AD_STATUS.ARCHIVED]: theme.inProgress,
			[AD_STATUS.ACTIVE]: theme.success,
			[AD_STATUS.DELETED]: theme.rejected,
			[AD_STATUS.PAUSED]: theme.retryingIcon,
		};

		return hexToRgb(statusColorCodeMap[status] ?? theme.retryingIcon, 0.2);
	};

	const normalizeConfiguredAdAccount = (account: IObjProps) => ({
		metaBusinessId: account?.metaBusinessId || '',
		metaBusinessName: account?.metaBusinessName || '',
		adAccountId: account?.adAccountId || '',
		adAccountName: account?.adAccountName || '',
	});

	const normalizeAvailableAdAccount = (businessAccount: IObjProps, account: IObjProps) => ({
		metaBusinessId: businessAccount.id,
		metaBusinessName: businessAccount.name,
		adAccountId: account?.account_id || '',
		adAccountName: account?.name || '',
	});

	const getConfiguredBusinessIds = (accounts: Array<IObjProps>) =>
		Array.from(new Set(accounts.map((account) => account.metaBusinessId).filter(Boolean)));

	const getSelectedAdAccountsFromMap = (adAccountsMap: Record<string, Array<IObjProps>>) =>
		Object.values(adAccountsMap)
			.flat()
			.filter((account) => selectedAdAccountIds.includes(account.adAccountId));

	const getConnectedAdsSummary = (accounts: Array<IObjProps>) =>
		accounts.length
			? accounts
					.map(
						(account) =>
							`${account.adAccountName || t('pages:broadcast.clickToWhatsappAds.adDetailsTab.adHeading')} (${account.adAccountId})`
					)
					.join(', ')
			: t(AD_DETAILS_LABELS.NO_CONNECTED_ADS);

	const getCurrencyPrefixedValue = (row: IObjProps, valueKey: string) => {
		const value = row[valueKey];
		const currencyCode = row.account_currency?.toUpperCase?.();
		const currencySymbol = currencyCode ? ACCOUNT_CURRENCY_SYMBOLS[currencyCode] : '';

		if (value === null || value === undefined || value === '') {
			return '-';
		}

		return currencySymbol ? `${currencySymbol} ${value}` : value;
	};

	const resetAdsConfiguration = () => {
		setAdDetails([]);
		setLastUpdatedOn('');
		setFbmAccounts([]);
		setSelectedBusinessIds([]);
		setAdAccountsByBusiness({});
		setSelectedAdAccountIds([]);
		setVisibleAdAccountCountByBusiness({});
		setConfiguredAdAccounts([]);
		setAdsSelection(false);
		setFbmSelection(false);
		displayAdsAnalytics(false);
		setSyncing(false);
	};

	const fetchConfiguredAdAccounts = () =>
		FB_API.getConfiguredAdAccounts().then((response: IObjProps) => {
			if (isValidResponseObject(response)) {
				const configuredAccounts = (response.data.responseObject || [])
					.map((account: IObjProps) => normalizeConfiguredAdAccount(account))
					.filter((account: IObjProps) => account.adAccountId);

				setConfiguredAdAccounts(configuredAccounts);
				setSelectedBusinessIds(getConfiguredBusinessIds(configuredAccounts));
				setSelectedAdAccountIds(configuredAccounts.map((account: IObjProps) => account.adAccountId));

				return configuredAccounts;
			}

			return Promise.reject(response);
		});

	const getLegacyConfiguredAccounts = (metaConfig: IObjProps = {}) => {
		const legacyAccount = normalizeConfiguredAdAccount(metaConfig);

		return legacyAccount.adAccountId ? [legacyAccount] : [];
	};

	const syncLegacyConfiguredAccounts = (legacyAccounts: Array<IObjProps>) => {
		if (
			!legacyAccounts.length ||
			!legacyAccounts.every(
				(account) =>
					account.metaBusinessId && account.metaBusinessName && account.adAccountId && account.adAccountName
			)
		) {
			return Promise.resolve(legacyAccounts);
		}

		return FB_API.saveBulkAdAccounts({
			adAccounts: legacyAccounts.map((account) => ({
				metaBusinessId: account.metaBusinessId,
				metaBusinessName: account.metaBusinessName,
				adAccountId: account.adAccountId,
				adAccountName: account.adAccountName,
			})),
		})
			.then(() => legacyAccounts)
			.catch(() => legacyAccounts);
	};

	const setConfiguredAccountsState = (accounts: Array<IObjProps>) => {
		setConfiguredAdAccounts(accounts);
		setSelectedBusinessIds(getConfiguredBusinessIds(accounts));
		setSelectedAdAccountIds(accounts.map((account) => account.adAccountId));
	};

	const fetchBusinessAccounts = (afterToken: string | null = null, accumulatedAccounts: Array<IObjProps> = []) =>
		FB_API.getBusinessAccounts(afterToken).then((response: IObjProps) => {
			if (!isValidResponseObject(response)) {
				return Promise.reject(response);
			}

			const currentPageAccounts = response.data.responseObject?.data || [];
			const allAccounts = [...accumulatedAccounts, ...currentPageAccounts];
			const nextAfterToken = response.data.responseObject?.paging?.cursors?.after || null;

			return nextAfterToken ? fetchBusinessAccounts(nextAfterToken, allAccounts) : allAccounts;
		});

	const getBusinessAccounts = ({ isSelectionScreenVisible = true }: { isSelectionScreenVisible?: boolean } = {}) => {
		setLoading(true);

		return fetchBusinessAccounts()
			.then((allAccounts: Array<IObjProps>) => {
				const normalizedAccounts = allAccounts
					.map((account) => ({
						id: account.id,
						name: account.name,
					}))
					.filter((account) => account.id);
				const availableBusinessIds = normalizedAccounts.map((account) => account.id);

				setFbmAccounts(normalizedAccounts);
				setSelectedBusinessIds((currentBusinessIds) => {
					const filteredCurrentIds = currentBusinessIds.filter((businessId) =>
						availableBusinessIds.includes(businessId)
					);

					if (filteredCurrentIds.length) {
						return filteredCurrentIds;
					}

					return getConfiguredBusinessIds(configuredAdAccounts).filter((businessId) =>
						availableBusinessIds.includes(businessId)
					);
				});
				setFbmSelection(isSelectionScreenVisible);
				setAdsSelection(false);

				return normalizedAccounts;
			})
			.catch(() => {
				toaster.negative(
					<ENGTToasterContainer title={t('common:error')} description={t('common:unableToFetchAccounts')} />,
					{}
				);

				return [];
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const fetchAdAccountsForBusiness = (
		businessAccount: IObjProps,
		afterToken: string | null = null,
		accumulatedAccounts: Array<IObjProps> = []
	) =>
		FB_API.getAdAccounts(businessAccount.id, afterToken).then((response: IObjProps) => {
			if (!isValidResponseObject(response)) {
				return Promise.reject(response);
			}

			const currentPageAccounts = response.data.responseObject?.data || [];
			const normalizedAccounts = currentPageAccounts
				.map((account: IObjProps) => normalizeAvailableAdAccount(businessAccount, account))
				.filter((account: IObjProps) => account.adAccountId);
			const allAccounts = [...accumulatedAccounts, ...normalizedAccounts];
			const nextAfterToken = response.data.responseObject?.paging?.cursors?.after || null;

			return nextAfterToken
				? fetchAdAccountsForBusiness(businessAccount, nextAfterToken, allAccounts)
				: allAccounts;
		});

	const getAdAccountsForSelectedBusinesses = (
		businessIds = selectedBusinessIds,
		{
			isSelectionScreenVisible = true,
			businessAccounts = fbmAccounts,
		}: { isSelectionScreenVisible?: boolean; businessAccounts?: Array<IObjProps> } = {}
	) => {
		const selectedBusinesses = businessAccounts.filter((account) => businessIds.includes(account.id));

		if (!selectedBusinesses.length) {
			setAdAccountsByBusiness({});
			setVisibleAdAccountCountByBusiness({});
			setAdsSelection(isSelectionScreenVisible);
			setFbmSelection(false);

			return Promise.resolve({});
		}

		setLoading(true);
		setVisibleAdAccountCountByBusiness({});

		return Promise.all(
			selectedBusinesses.map((businessAccount) =>
				fetchAdAccountsForBusiness(businessAccount).then((accounts: Array<IObjProps>) => ({
					businessId: businessAccount.id,
					accounts,
				}))
			)
		)
			.then((groups: Array<{ businessId: string; accounts: Array<IObjProps> }>) => {
				const nextAdAccountsByBusiness = groups.reduce(
					(
						accumulator: Record<string, Array<IObjProps>>,
						group: { businessId: string; accounts: Array<IObjProps> }
					) => {
						accumulator[group.businessId] = group.accounts;

						return accumulator;
					},
					{}
				);
				const availableAccountIds = groups.flatMap(
					(group: { businessId: string; accounts: Array<IObjProps> }) =>
						group.accounts.map((account: IObjProps) => account.adAccountId)
				);

				setAdAccountsByBusiness(nextAdAccountsByBusiness);
				setSelectedAdAccountIds((currentAccountIds) => {
					const filteredCurrentIds = currentAccountIds.filter((accountId) =>
						availableAccountIds.includes(accountId)
					);

					if (filteredCurrentIds.length) {
						return filteredCurrentIds;
					}

					return configuredAdAccounts
						.map((account) => account.adAccountId)
						.filter((accountId) => availableAccountIds.includes(accountId));
				});
				setAdsSelection(isSelectionScreenVisible);
				setFbmSelection(false);

				return nextAdAccountsByBusiness;
			})
			.catch(() => {
				toaster.negative(
					<ENGTToasterContainer
						title={t('common:error')}
						description={t('common:unableToFetchAdAccounts')}
					/>,
					{}
				);

				return {};
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const getAdConfiguration = () => {
		setLoading(true);

		FB_API.getBotMetaConfig()
			.then((metaResponse: IObjProps) => {
				const metaConfig = isValidResponseObject(metaResponse) ? metaResponse.data.responseObject || {} : {};
				const legacyConfiguredAccounts = getLegacyConfiguredAccounts(metaConfig);
				const authToken = metaConfig?.adAuthToken;

				if (!authToken) {
					setIsAdAuthTokenPresent(false);

					return null;
				}

				setIsAdAuthTokenPresent(true);

				return getBusinessAccounts({ isSelectionScreenVisible: false }).then(
					(businessAccounts: Array<IObjProps>) =>
						fetchConfiguredAdAccounts()
							.catch(() => [])
							.then((configuredAccounts: Array<IObjProps>) => {
								if (configuredAccounts.length) {
									const configuredBusinessIds = getConfiguredBusinessIds(configuredAccounts);

									setConfiguredAccountsState(configuredAccounts);
									setFbmSelection(false);
									setAdsSelection(false);

									return getAdAccountsForSelectedBusinesses(configuredBusinessIds, {
										isSelectionScreenVisible: false,
										businessAccounts,
									}).then(() => {
										displayAdsAnalytics(true);
										setSyncing(false);

										return getAdAnalyticsData(0, pageDefaultSize);
									});
								}

								if (legacyConfiguredAccounts.length) {
									return syncLegacyConfiguredAccounts(legacyConfiguredAccounts).then(
										(syncedAccounts: Array<IObjProps>) => {
											const syncedBusinessIds = getConfiguredBusinessIds(syncedAccounts);

											setConfiguredAccountsState(syncedAccounts);
											setFbmSelection(false);
											setAdsSelection(false);

											return getAdAccountsForSelectedBusinesses(syncedBusinessIds, {
												isSelectionScreenVisible: false,
												businessAccounts,
											}).then(() => {
												displayAdsAnalytics(true);
												setSyncing(false);

												return getAdAnalyticsData(0, pageDefaultSize);
											});
										}
									);
								}

								displayAdsAnalytics(false);
								setFbmSelection(true);
								setAdsSelection(false);

								return businessAccounts;
							})
				);
			})
			.catch(() => {
				toaster.negative(
					<ENGTToasterContainer title={t('common:error')} description={t('common:somethingWentWrong')} />,
					{}
				);
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const getMessagesFilterKey = `all_${botRef}_${uid}`;
	const getLiteMessagesFilterKey = `oneviewlite_${botRef}_${uid}`;
	const usersFilterKey = `userFilter_${botRef}_${uid}`;
	const messageSearchQueryKey = `message_search_query_${botRef}_${uid}`;
	const messageSearchTagKey = `message_search_tag_${botRef}_${uid}`;
	const oneViewLiteQuickFilterKey = `oneviewlite_${botRef}_${uid}_quick_filters`;

	const deleteFbAdToken = () => {
		setLoading(true);

		FB_API.deleteAdAuthToken()
			.then((response: IObjProps) => {
				if (isValidResponseObject(response)) {
					toaster.positive(
						<ENGTToasterContainer
							description={t('common:changesSuccessfulSaved')}
							title={t('pages:configure.deployment.facebookDeployment.connected.removeAccountHeading')}
						/>,
						{}
					);
					setIsAdAuthTokenPresent(false);
					resetAdsConfiguration();
				} else {
					setIsAdAuthTokenPresent(true);
					toaster.negative(
						<ENGTToasterContainer
							title={t('pages:configure.deployment.facebookDeployment.authFailed')}
							description={t('common:somethingWentWrong')}
						/>,
						{}
					);
				}
			})
			.catch(() => {
				toaster.negative(
					<ENGTToasterContainer
						title={t('pages:configure.deployment.facebookDeployment.authFailed')}
						description={t('common:somethingWentWrong')}
					/>,
					{}
				);
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const saveConfiguredAdAccounts = () => {
		const selectedAccounts = getSelectedAdAccountsFromMap(adAccountsByBusiness);
		const payload = {
			adAccounts: selectedAccounts.map((account) => ({
				metaBusinessId: account.metaBusinessId,
				metaBusinessName: account.metaBusinessName,
				adAccountId: account.adAccountId,
				adAccountName: account.adAccountName,
			})),
		};

		setLoading(true);

		FB_API.saveBulkAdAccounts(payload)
			.then((response: IObjProps) => {
				if (isValidResponseObject(response)) {
					return fetchConfiguredAdAccounts()
						.catch(() => selectedAccounts)
						.then((configuredAccounts: Array<IObjProps>) => {
							const resolvedAccounts = configuredAccounts.length ? configuredAccounts : selectedAccounts;

							setConfiguredAccountsState(resolvedAccounts);
							setAdsSelection(false);
							setFbmSelection(false);
							displayAdsAnalytics(false);
							setSyncing(true);
							getAdAnalyticsDataAfterDelay();

							return resolvedAccounts;
						});
				}
				toaster.negative(
					<ENGTToasterContainer title={t('common:error')} description={t('common:somethingWentWrong')} />,
					{}
				);
			})
			.catch(() => {
				toaster.negative(
					<ENGTToasterContainer title={t('common:error')} description={t('common:somethingWentWrong')} />,
					{}
				);
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const callOnSignIn = () => {
		setIsAdAuthTokenPresent(true);
		getBusinessAccounts({ isSelectionScreenVisible: true });
	};

	useEffect(() => {
		getAdConfiguration();
	}, []);

	const adDetailsColumns = [
		{
			id: 'ad_name',
			span: 1.2,
			name: t('pages:broadcast.clickToWhatsappAds.adName'),
			processor: (data: IObjProps, row: IObjProps) => row.ad_name ?? '-',
		},
		{
			id: 'ad_status',
			span: 0.9,
			name: t('pages:broadcast.clickToWhatsappAds.adStatus'),
			processor: (data: IObjProps, row: IObjProps) => (
				<span
					className={css({
						position: 'relative',
						backgroundColor: getStatusColorCode(row.status),
						padding: '0.25rem 0.4375rem',
						borderRadius: '0.25rem',
						cursor: 'pointer',
					})}
				>
					{row.status ?? '-'}
				</span>
			),
		},
		{
			id: 'ad_type',
			span: 0.7,
			name: t(AD_DETAILS_LABELS.AD_TYPE),
			processor: (data: IObjProps, row: IObjProps) =>
				row.ad_type && Object.values(AD_TYPE).includes(row.ad_type) ? row.ad_type : '-',
		},
		{
			id: 'ad_account_id',
			span: 1.2,
			name: t(AD_DETAILS_LABELS.AD_ACCOUNT_ID),
			processor: (data: IObjProps, row: IObjProps) => row.ad_account_id || row.adAccountId || '-',
		},
		{
			id: 'impressions',
			span: 0.9,
			name: t('pages:broadcast.clickToWhatsappAds.impressions'),
			tooltip: t('pages:broadcast.clickToWhatsappAds.impressionsTooltip'),
			processor: (data: IObjProps, row: IObjProps) => row.impressions ?? '-',
		},
		{
			id: 'reach',
			span: 0.7,
			name: t('pages:broadcast.clickToWhatsappAds.reach'),
			tooltip: t('pages:broadcast.clickToWhatsappAds.reachTooltip'),
			processor: (data: IObjProps, row: IObjProps) => row.reach ?? '-',
		},
		{
			id: 'clicks',
			span: 0.9,
			name: t('pages:broadcast.clickToWhatsappAds.clicksOnAd'),
			tooltip: t('pages:broadcast.clickToWhatsappAds.clicksOnAdTooltip'),
			processor: (data: IObjProps, row: IObjProps) => row.clicks ?? '-',
		},
		{
			id: 'ctr',
			span: 0.7,
			name: t('pages:broadcast.clickToWhatsappAds.ctr'),
			tooltip: t('pages:broadcast.clickToWhatsappAds.ctrTooltip'),
			processor: (data: IObjProps, row: IObjProps) => row.ctr ?? '-',
		},
		{
			id: 'cpc',
			span: 1,
			name: t(AD_DETAILS_LABELS.COST_PER_CLICK),
			tooltip: t(AD_DETAILS_LABELS.COST_PER_CLICK_TOOLTIP),
			processor: (data: IObjProps, row: IObjProps) => getCurrencyPrefixedValue(row, 'cpc'),
		},
		{
			id: 'spend',
			span: 1.1,
			name: t(AD_DETAILS_LABELS.TOTAL_AMOUNT_SPENT),
			tooltip: t(AD_DETAILS_LABELS.TOTAL_AMOUNT_SPENT_TOOLTIP),
			processor: (data: IObjProps, row: IObjProps) => getCurrencyPrefixedValue(row, 'spend'),
		},
		{
			id: 'leads_captured_wa',
			span: 1.1,
			name: t('pages:broadcast.clickToWhatsappAds.usersEngagedOnWhatsApp'),
			tooltip: t('pages:broadcast.clickToWhatsappAds.usersEngagedOnWhatsAppTooltip'),
			processor: (data: IObjProps, row: IObjProps) => row.leads_captured_wa ?? '-',
		},
		{
			id: 'leads_captured_insta',
			span: 1.2,
			name: t('pages:broadcast.clickToWhatsappAds.usersEngagedOnInstagram'),
			tooltip: t('pages:broadcast.clickToWhatsappAds.usersEngagedOnInstagramTooltip'),
			processor: (data: IObjProps, row: IObjProps) => row.leads_captured_insta ?? '-',
		},
	];
	const navigateToUsersList = (adId: string) => {
		const filter = 'filter=fromCTWA';
		const solrQuery = `source_ids:"${adId}"`;
		const query = `query=${encodeURIComponent(solrQuery)}`;

		localStorage.setItem(
			usersFilterKey,
			JSON.stringify({
				filter: solrQuery,
				filterState: solrQuery,
			})
		);

		navigate(`${usersListLink}?${filter}&${query}`);
	};

	const navigateToMessagesList = (adId: string) => {
		const filters = {
			facets: {
				customFilterSelected: {
					botRef,
					customerId,
					filterExpression: 'A1',
					expression: `source_ids contains ${adId}`,
					solrQuery: `source_ids:"${adId}"`,
					type: 'USER',
					rules: {
						A1: {
							lhOperand: 'source_ids',
							rhOperand: adId,
							operator: 'IS_EQUAL_TO',
						},
					},
					isChecked: true,
				},
			},
			sort: messageSortAllScreen.recentFirst,
		};

		localStorage.setItem(getMessagesFilterKey, JSON.stringify(filters));
		localStorage.setItem(getLiteMessagesFilterKey, JSON.stringify(filters));
		localStorage.setItem(
			oneViewLiteQuickFilterKey,
			JSON.stringify({
				myConversations: false,
				unreadMessages: false,
				starredConversations: false,
			})
		);
		localStorage.removeItem(messageSearchQueryKey);
		localStorage.removeItem(messageSearchTagKey);

		const messagesBasePath = isSimplifiedOneViewEnabled
			? TwoColumnRouteLinks.simplifiedMessages
			: TwoColumnRouteLinks.messagesAllLink;

		navigate(`${messagesBasePath}?filter_applied=true&listOnly=true`);
	};

	const getAdActionItems = (row: IObjProps): ITableRowAction[] => {
		const adId = row.ad_id;
		const status = row.status?.toUpperCase();

		const actions: ITableRowAction[] = [
			{
				label: t(AD_DETAILS_LABELS.RETARGET),
				onClick: () => {
					setSelectedAdId(adId);
					onRetargetSegment(adId);
				},
			},
			{
				label: t(AD_DETAILS_LABELS.VIEW_USERS),
				onClick: () => navigateToUsersList(adId),
			},
			{
				label: t(AD_DETAILS_LABELS.VIEW_MESSAGES),
				onClick: () => navigateToMessagesList(adId),
			},
			{
				label: t(AD_DETAILS_LABELS.EDIT),
				onClick: () => {
					window.open(`${ADS_MANAGER_URL + adId}&env=${findEnv()}`, '_blank');
				},
			},
		];

		switch (status) {
			case AD_STATUS.ACTIVE:
				actions.push(
					{
						label: t(AD_DETAILS_LABELS.PAUSE),
						onClick: () => updateMetaAdStatus(adId, AD_STATUS.PAUSED),
					},
					{
						label: t(AD_DETAILS_LABELS.ARCHIVE),
						onClick: () => updateMetaAdStatus(adId, AD_STATUS.ARCHIVED),
					},
					{
						label: t(AD_DETAILS_LABELS.DELETE),
						onClick: () => updateMetaAdStatus(adId, AD_STATUS.DELETED),
					}
				);
				break;

			case AD_STATUS.PAUSED:
				actions.push(
					{
						label: t(AD_DETAILS_LABELS.ACTIVATE),
						onClick: () => updateMetaAdStatus(adId, AD_STATUS.ACTIVE),
					},
					{
						label: t(AD_DETAILS_LABELS.ARCHIVE),
						onClick: () => updateMetaAdStatus(adId, AD_STATUS.ARCHIVED),
					},
					{
						label: t(AD_DETAILS_LABELS.DELETE),
						onClick: () => updateMetaAdStatus(adId, AD_STATUS.DELETED),
					}
				);
				break;

			case AD_STATUS.ARCHIVED:
				actions.push({
					label: t(AD_DETAILS_LABELS.DELETE),
					onClick: () => updateMetaAdStatus(adId, AD_STATUS.DELETED),
				});
				break;
		}

		return actions;
	};

	const rowActions = (row: IObjProps): ITableRowAction[] => [
		{
			icon: <Overflow size={24} />,
			items: getAdActionItems(row),
			onClick: () => {},
		},
	];

	const loadMoreAdsFn = (pageNo = 1, size: number = pageDefaultSize) => {
		loadMoreAds({
			page: pageNo,
			size,
		});
	};

	const paginationMethods = usePaginationHook({
		onChange: loadMoreAdsFn,
	});

	const { setPagination } = paginationMethods;

	const loadMoreAds = (params: IObjProps) => {
		const { page, size } = params;
		getAdAnalyticsData(page - 1, Math.min(size, MAX_PAGE_SIZE));
	};

	const getAdAnalyticsDataAfterDelay = () => {
		setTimeout(() => {
			setSyncing(false);
			displayAdsAnalytics(true);
			getAdAnalyticsData(0, pageDefaultSize);
		}, 30000);
	};

	const onSortingBroadcast = (data: string) => {
		setSorting(data);
		getAdAnalyticsData(0, pageDefaultSize, data);
	};

	const getAdAnalyticsData = (page: number, size: number, sortOption?: string) => {
		setLoading(true);

		const pagesToFetch = Math.min(size, MAX_PAGE_SIZE);

		const params = {
			page,
			size: pagesToFetch,
			sortOption: sortOption || sort,
			targetApi: 'getAdLevelAnalytics',
		};

		return API.getAdLevelAnalytics(params)
			.then((response: IObjProps) => {
				if (isValidResponseObject(response)) {
					const { responseObject } = response.data;
					const adLevelAnalyticsResponse = responseObject?.adLevelAnalyticsResponsePage;
					const paging = {
						size: adLevelAnalyticsResponse?.size,
						page: adLevelAnalyticsResponse?.pageable?.pageNumber + 1,
						totalPages: adLevelAnalyticsResponse?.totalPages,
						totalElements: adLevelAnalyticsResponse?.totalElements,
					};

					setPagination(paging);
					setLastUpdatedOn(responseObject?.lastUpdatedOn);
					setAdDetails(adLevelAnalyticsResponse?.content || []);
				} else {
					toaster.negative(
						<ENGTToasterContainer title={t('common:error')} description={t('common:somethingWentWrong')} />,
						{}
					);
				}
			})
			.catch(() => {
				toaster.negative(
					<ENGTToasterContainer title={t('common:error')} description={t('common:somethingWentWrong')} />,
					{}
				);
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const onRetargetSegment = (adId: string) => {
		API.retargetForAd(adId).then((response: IObjProps) => {
			if (isValidResponseObject(response)) {
				const data = response.data.responseObject;
				setSegmentData(data);

				if (data.insta_users_segment_id && data.all_users_count > 0) {
					setPlatformSelectionModalOpen(true);
				} else {
					setRetargetModalOpen(true);
				}
			} else {
				toaster.negative(
					<ENGTToasterContainer title={t('common:error')} description={t('common:somethingWentWrong')} />,
					{}
				);
			}
		});
	};

	const handlePlatformSelection = (platform: string, data: IObjProps) => {
		if (platform === CHANNELS.WHATSAPP) {
			setRetargetModalOpen(true);
		} else if (platform === CHANNELS.INSTAGRAM) {
			const broadcastData = {
				broadcastName: data.insta_users_segment_name,
				segmentId: data.insta_users_segment_id,
				segmentName: data.insta_users_segment_name,
				platform: CHANNELS.INSTAGRAM,
			};

			const queryString = `mode=${BROADCAST_TYPE.CREATE}&payload=${encodeURIComponent(
				JSON.stringify(broadcastData)
			)}&path=instagram`;
			navigate(`${ThreeColumnRouteLinks.createBroadcast}?${queryString}`);
		}
	};

	const updateMetaAdStatus = (adId: string, status: string) => {
		API.updatedAdStatus(adId, status).then((response: IObjProps) => {
			if (isValidResponseObject(response)) {
				toaster.positive(
					<ENGTToasterContainer
						description={t('common:changesSuccessfulSaved')}
						title={t('common:success')}
					/>,
					{}
				);
				getAdAnalyticsData(0, pageDefaultSize);
			} else {
				toaster.negative(
					<ENGTToasterContainer title={t('common:error')} description={t('common:somethingWentWrong')} />,
					{}
				);
			}
		});
	};

	const connectedAdsSummary = getConnectedAdsSummary(configuredAdAccounts);
	const connectedAdsCount = configuredAdAccounts.length;
	const groupedAdAccounts = selectedBusinessIds
		.map((businessId) => ({
			businessId,
			businessName: fbmAccounts.find((account) => account.id === businessId)?.name || businessId,
			adAccounts: adAccountsByBusiness[businessId] || [],
		}))
		.filter((businessGroup) => businessGroup.adAccounts.length > 0);

	const toggleBusinessSelection = (businessId: string, isChecked: boolean) => {
		setSelectedBusinessIds((currentBusinessIds) =>
			isChecked
				? Array.from(new Set([...currentBusinessIds, businessId]))
				: currentBusinessIds.filter((id) => id !== businessId)
		);

		if (!isChecked) {
			setSelectedAdAccountIds((currentAdAccountIds) =>
				currentAdAccountIds.filter(
					(adAccountId) =>
						!(adAccountsByBusiness[businessId] || []).some((account) => account.adAccountId === adAccountId)
				)
			);
		}
	};

	const toggleAdAccountSelection = (adAccountId: string, isChecked: boolean) => {
		setSelectedAdAccountIds((currentAdAccountIds) =>
			isChecked
				? Array.from(new Set([...currentAdAccountIds, adAccountId]))
				: currentAdAccountIds.filter((id) => id !== adAccountId)
		);
	};

	const getVisibleAdAccountCount = (businessId: string) =>
		visibleAdAccountCountByBusiness[businessId] || DEFAULT_VISIBLE_AD_ACCOUNT_COUNT;

	const showMoreAdAccounts = (businessId: string) => {
		setVisibleAdAccountCountByBusiness((currentVisibleCounts) => ({
			...currentVisibleCounts,
			[businessId]:
				(currentVisibleCounts[businessId] || DEFAULT_VISIBLE_AD_ACCOUNT_COUNT) +
				DEFAULT_VISIBLE_AD_ACCOUNT_COUNT,
		}));
	};

	const onReconfigureConnectedAds = () => {
		setLoading(true);

		const connectedBusinessIds = getConfiguredBusinessIds(configuredAdAccounts);
		const configuredAccountIds = configuredAdAccounts.map((account) => account.adAccountId);

		getBusinessAccounts({ isSelectionScreenVisible: true })
			.then((businessAccounts: Array<IObjProps>) => {
				if (!businessAccounts.length) {
					return null;
				}

				setSelectedBusinessIds(connectedBusinessIds);
				setSelectedAdAccountIds(configuredAccountIds);
				setAdAccountsByBusiness({});
				setVisibleAdAccountCountByBusiness({});
				displayAdsAnalytics(false);
				setAdsSelection(false);
				setFbmSelection(true);

				return businessAccounts;
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const openLogoutConfirmation = () => {
		setLogoutModalOpen(true);
	};

	const confirmLogout = () => {
		setLogoutModalOpen(false);
		deleteFbAdToken();
	};

	const showFbmSelection = !isLoading && isAdAuthTokenPresent && fbmSelection && !adsSelection;
	const showAdsSelection = !isLoading && isAdAuthTokenPresent && !fbmSelection && adsSelection;
	const showAdsAnalytics = !isLoading && isAdAuthTokenPresent && adsAnalytics;
	const showSyncingScreen = !isLoading && isAdAuthTokenPresent && syncing;

	const renderBusinessAccounts = () =>
		fbmAccounts?.length > 0 ? (
			<div className={css({ marginTop: '0.875rem' })}>
				<FormLabel
					id='upload-files'
					label={t('pages:broadcast.clickToWhatsappAds.adDetailsTab.fbmHeading')}
					className={{ marginTop: '1rem', marginBottom: '0.5rem', fontSize: '1rem', fontWeight: 500 }}
					tooltip={t('pages:broadcast.clickToWhatsappAds.adDetailsTab.fbmTooltip')}
				/>
				<div
					className={css({
						maxHeight: '20rem',
						overflowY: 'auto',
					})}
				>
					<div
						className={css({
							display: 'flex',
							flexDirection: 'column',
							gap: '0.625rem',
						})}
					>
						{fbmAccounts.map((option) => (
							<Checkbox
								key={option.id}
								values={{
									isChecked: selectedBusinessIds.includes(option.id),
									label: `${option.name} | ${t(
										'pages:configure.deployment.whatsappDeployment.waCommerce.businessId'
									)}${option.id}`,
								}}
								onChange={(isChecked) => toggleBusinessSelection(option.id, isChecked)}
							/>
						))}
					</div>
				</div>
			</div>
		) : (
			renderNoData('fbm')
		);

	const renderAdsAccounts = () =>
		selectedBusinessIds.length > 0 && groupedAdAccounts.length > 0 ? (
			<div
				className={css({
					marginTop: '0.875rem',
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
					minHeight: 0,
				})}
			>
				<FormLabel
					id='upload-files'
					label={t(AD_DETAILS_LABELS.CONNECTED_ADS)}
					className={{ marginTop: '1rem', marginBottom: '0.5rem', fontSize: '1rem', fontWeight: 500 }}
					tooltip={t('pages:broadcast.clickToWhatsappAds.adDetailsTab.adTooltip')}
				/>
				<div
					className={css({
						display: 'flex',
						flexDirection: 'column',
						gap: '1rem',
						flex: 1,
						minHeight: 0,
						overflowY: 'auto',
						width: '100%',
					})}
				>
					{groupedAdAccounts.map((businessGroup, index) => (
						<div
							key={businessGroup.businessId}
							className={css({
								width: '100%',
								display: 'flex',
								flexDirection: 'column',
								gap: '0.875rem',
								paddingBottom: '1.25rem',
								borderBottom:
									index !== groupedAdAccounts.length - 1
										? `1px solid ${theme.colors.borderOpaque}`
										: 'none',
							})}
						>
							<div
								className={css({
									fontSize: '0.95rem',
									fontWeight: 600,
									color: theme.colors.primaryA,
								})}
							>
								{businessGroup.businessName}
							</div>
							<div
								className={css({
									display: 'flex',
									flexDirection: 'column',
									gap: '0.5rem',
									width: '100%',
								})}
							>
								{businessGroup.adAccounts
									.slice(0, getVisibleAdAccountCount(businessGroup.businessId))
									.map((account) => (
										<Checkbox
											key={account.adAccountId}
											values={{
												isChecked: selectedAdAccountIds.includes(account.adAccountId),
												label: `${account.adAccountName} | ${t(
													AD_DETAILS_LABELS.AD_ACCOUNT_ID
												)}${account.adAccountId}`,
											}}
											onChange={(isChecked) =>
												toggleAdAccountSelection(account.adAccountId, isChecked)
											}
										/>
									))}
							</div>
							{businessGroup.adAccounts.length > getVisibleAdAccountCount(businessGroup.businessId) && (
								<button
									type='button'
									onClick={() => showMoreAdAccounts(businessGroup.businessId)}
									className={css({
										alignSelf: 'flex-start',
										marginTop: '0.25rem',
										display: 'inline-flex',
										alignItems: 'center',
										columnGap: '0.25rem',
										padding: 0,
										border: 'none',
										backgroundColor: 'transparent',
										color: theme.colors.accent,
										cursor: 'pointer',
										fontSize: '0.875rem',
										fontWeight: 600,
									})}
								>
									<span>{t('pages:messages.showMore')}</span>
									<ChevronDown size={18} color={theme.colors.accent} />
								</button>
							)}
						</div>
					))}
				</div>
			</div>
		) : (
			renderNoData('ads')
		);

	const renderNoData = (value: string) => (
		<NoDataTemplate
			bannerImage={{
				altText: t('pages:broadcast.clickToWhatsappAds.adDetailsTab.noData'),
				imageUrl: NoDataEmoji,
			}}
			heading={
				value === 'fbm'
					? t('pages:broadcast.clickToWhatsappAds.adDetailsTab.noFbmData')
					: t('pages:broadcast.clickToWhatsappAds.adDetailsTab.noAdAccountsData')
			}
			headingCss={{ marginTop: '1rem' }}
		/>
	);

	const displayLogin = () => (
		<div
			className={css({
				display: 'flex',
				justifyContent: 'center',
				flexDirection: 'column',
				flexWrap: 'wrap',
				alignItems: 'center',
				height: '100%',
			})}
		>
			<div
				className={css({
					fontWeight: '700',
					fontSize: '1.25rem',
					marginBottom: '0.6rem',
					color: theme.colors.primaryA,
				})}
			>
				{' '}
				{t('pages:broadcast.clickToWhatsappAds.adDetailsTab.loginHeading1')}{' '}
			</div>
			<div
				className={css({
					fontWeight: '400',
					fontSize: '1rem',
					marginBottom: '0.6rem',
					color: theme.colors.primaryA,
				})}
			>
				{' '}
				{t('pages:broadcast.clickToWhatsappAds.adDetailsTab.loginHeading2')}{' '}
			</div>

			<LoginComponent
				botKey={botKey}
				appId={appId}
				appName='ads'
				label={t('pages:broadcast.clickToWhatsappAds.adDetailsTab.signIn')}
				setReauthorized={setLoading}
				css={css}
				theme={theme}
				setIsAdAuthTokenPresent={setIsAdAuthTokenPresent}
				callOnSignIn={callOnSignIn}
			/>
		</div>
	);

	const renderSyncingScreen = () => (
		<div
			className={css({
				display: 'flex',
				justifyContent: 'center',
				flexDirection: 'row',
				flexWrap: 'wrap',
				alignItems: 'center',
				height: '100%',
			})}
		>
			<div
				className={css({
					fontWeight: '700',
					fontSize: '1.25rem',
					marginRight: '0.6rem',
					color: theme.colors.primaryA,
				})}
			>
				{t('pages:broadcast.clickToWhatsappAds.adDetailsTab.syncing')}
			</div>
			<BouncingEffect backgroundColor={theme.failure} />
		</div>
	);

	const renderAdsAnalytics = () => (
		<>
			<div
				className={css({
					display: 'flex',
					flexDirection: 'column',
					gap: '0.75rem',
					marginBottom: '0.5rem',
				})}
			>
				<div
					className={css({
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'flex-start',
						gap: '1rem',
						flexWrap: 'wrap',
					})}
				>
					<div
						className={css({
							display: 'flex',
							flexDirection: 'column',
							gap: '0.625rem',
							maxWidth: '60rem',
						})}
					>
						{lastUpdatedOn && (
							<div
								className={css({
									fontWeight: 400,
									fontSize: '0.875rem',
									alignItems: 'center',
									color: theme.colors.primaryA,
									display: 'flex',
								})}
							>
								{t('components:conversations.lastUpdatedOn')}
								<span style={{ marginLeft: '0.2rem' }}>{`${t(
									'common:standardDateTimeFormatterWithShortMonthNameAndTimeAheadOfDate',
									{
										date: lastUpdatedOn,
									}
								)}`}</span>
								{tableInfoIcon()}
							</div>
						)}
						<div
							className={css({
								fontWeight: 600,
								fontSize: '1rem',
								color: theme.colors.primaryA,
							})}
						>
							{t(AD_DETAILS_LABELS.CONNECTED_ADS_SELECTED, {
								count: connectedAdsCount,
							})}
						</div>
					</div>
					<ENGTButton
						kind={KIND.secondary}
						label={t(AD_DETAILS_LABELS.RECONFIGURE)}
						onClick={onReconfigureConnectedAds}
					/>
				</div>
				<div
					className={css({
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'flex-start',
						gap: '0.75rem',
						flexWrap: 'wrap',
					})}
				>
					<div
						className={css({
							flex: '1 1 48rem',
							padding: '0.75rem 1rem',
							borderRadius: '0.75rem',
							backgroundColor: 'rgba(0, 0, 0, 0.2)',
							border: `1px solid ${theme.colors.borderOpaque}`,
							fontSize: '0.95rem',
							fontWeight: 500,
							lineHeight: '1.5rem',
							color: theme.colors.primaryA,
							whiteSpace: 'normal',
							wordBreak: 'break-word',
						})}
					>
						{connectedAdsSummary}
					</div>
					<AdsSorting initialValue={sort} sortOptions={sortOptions} onSortValueChange={onSortingBroadcast} />
				</div>
			</div>

			<ENGTTable
				columns={adDetailsColumns}
				rows={adDetails}
				pagination={paginationMethods}
				paginationType='footer'
				customSizeOptions={AD_PAGE_SIZE_OPTIONS}
				overrides={tableOverrides}
				rowActions={rowActions}
				loader={isLoading}
			/>
		</>
	);

	const tableInfoIcon = () => (
		<ENGTTooltip content={t('pages:broadcast.clickToWhatsappAds.adDetailsTab.tableMetricsInfo')}>
			<label
				style={{
					cursor: 'pointer',
				}}
			>
				<InfoIcon fill={theme.colors.accent50} />
			</label>
		</ENGTTooltip>
	);

	return (
		<div
			className={css({
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'space-between',
			})}
		>
			<LayoutContent>
				{isLoading && <Loader loaderBlockHeight='50%' />}
				{!isLoading && !isAdAuthTokenPresent && displayLogin()}
				{showFbmSelection && renderBusinessAccounts()}
				{showAdsSelection && renderAdsAccounts()}
				{showAdsAnalytics && renderAdsAnalytics()}
				{showSyncingScreen && renderSyncingScreen()}
			</LayoutContent>
			<LayoutFooter>
				<div className={css({ display: 'flex', columnGap: '0.5rem' })}>
					{showFbmSelection && (
						<>
							<ENGTButton
								type='submit'
								label={t('common:next')}
								isDisabled={!selectedBusinessIds.length}
								className={css({
									marginTop: '0.5rem',
								})}
								onClick={() => {
									getAdAccountsForSelectedBusinesses();
								}}
								dataQa={AD_DETAILS_NEXT}
							/>
							<ENGTButton
								kind={KIND.secondary}
								label={t('common:logout')}
								onClick={openLogoutConfirmation}
								className={css({
									marginTop: '0.5rem',
								})}
								dataQa={AD_DETAILS_LOGOUT}
							/>
						</>
					)}

					{showAdsSelection && (
						<>
							<ENGTButton
								type='submit'
								label={t('pages:broadcast.clickToWhatsappAds.adDetailsTab.connect')}
								isDisabled={!selectedAdAccountIds.length}
								onClick={() => {
									saveConfiguredAdAccounts();
								}}
								dataQa={AD_DETAILS_CONNECT}
							/>

							<ENGTButton
								type='submit'
								label={t('common:previous')}
								kind={KIND.secondary}
								onClick={() => {
									setAdsSelection(false);
									setFbmSelection(true);
								}}
								dataQa={AD_DETAILS_PREVIOUS}
							/>
							<ENGTButton
								kind={KIND.secondary}
								label={t('common:logout')}
								onClick={openLogoutConfirmation}
								dataQa={AD_DETAILS_LOGOUT}
							/>
						</>
					)}

					{showAdsAnalytics && (
						<ENGTButton
							kind={KIND.secondary}
							label={t('common:logout')}
							onClick={openLogoutConfirmation}
							dataQa={AD_DETAILS_LOGOUT}
						/>
					)}
				</div>
			</LayoutFooter>
			<ActionModal
				heading={t(AD_DETAILS_LABELS.LOGOUT_MODAL_HEADING)}
				isOpen={isLogoutModalOpen}
				onClose={() => setLogoutModalOpen(false)}
				onConfirm={confirmLogout}
				confirmBtnLabel={t('common:logout')}
				closeBtnLabel={t('common:cancel')}
				autoFocus={false}
			>
				<div>{t(AD_DETAILS_LABELS.LOGOUT_MODAL_DESCRIPTION)}</div>
			</ActionModal>
			{isPlatformSelectionModalOpen && segmentData && (
				<RetargetPlatformSelectionModal
					isModalOpen={setPlatformSelectionModalOpen}
					segmentData={segmentData}
					onPlatformSelect={handlePlatformSelection}
				/>
			)}
			{isRetargetModalOpen && segmentData && (
				<ClickToWARetargetModal
					isModalOpen={setRetargetModalOpen}
					adId={selectedAdId}
					segmentData={segmentData}
				/>
			)}
		</div>
	);
};

export default AdDetailsTab;
